import { defineStore } from 'pinia';
import { router } from '@/router';
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';
import { SecurityValidator } from '@/utils/security';
import { store as vuexStore } from '@/storeVuex';
import { socketService } from '@/services/socketService';
import { identifySentryUser, resetSentryUser } from '@/plugins/monitoring';
import { useAnalytics } from '@/composables/useAnalytics';
import { safeClearLocalStorage } from '@/utils/safeClearStorage';

const baseUrl = `${import.meta.env.VITE_API_URL}/users`;

export const useAuthStore = defineStore({
    id: 'auth',
    state: () => {
        // First try to get user from Vuex store (encrypted storage)
        let user = null;
        if (vuexStore && vuexStore.state && vuexStore.state.user && vuexStore.state.user.user) {
            // If Vuex has user data, use it and sync to localStorage
            const vuexUser = vuexStore.state.user;
            user = {
                ...vuexUser.user,
                token: vuexUser.token,
                loginTime: Date.now(),
                sessionId: SecurityValidator.generateCSRFToken()
            };
            // Sync to localStorage for Pinia
            localStorage.setItem('user', JSON.stringify(user));
        } else {
            // Fallback to localStorage if Vuex doesn't have user data
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    user = JSON.parse(storedUser);
                } catch (e) {
                    console.error('Failed to parse stored user data:', e);
                    user = null;
                }
            }
        }

        return {
            // initialize state from Vuex or local storage to enable user to stay logged in
            user: user,
            returnUrl: null,
            loginAttempts: 0,
            lastLoginAttempt: null as number | null,
            csrfToken: null as string | null
        };
    },
    actions: {
        syncWithVuexStore() {
            // Sync Pinia store with Vuex store
            if (vuexStore && vuexStore.state && vuexStore.state.user && vuexStore.state.user.user) {
                const vuexUser = vuexStore.state.user;
                this.user = {
                    ...vuexUser.user,
                    token: vuexUser.token,
                    loginTime: this.user?.loginTime || Date.now(),
                    sessionId: this.user?.sessionId || SecurityValidator.generateCSRFToken(),
                    csrfToken: this.csrfToken
                };
                // Update localStorage to keep in sync
                localStorage.setItem('user', JSON.stringify(this.user));
                return true;
            }
            return false;
        },

        generateCSRFToken() {
            this.csrfToken = SecurityValidator.generateCSRFToken();
            return this.csrfToken;
        },

        async login(username: string, password: string) {
            // Validate input
            const sanitizedUsername = SecurityValidator.sanitizeText(username);
            if (!SecurityValidator.validateEmail(sanitizedUsername)) {
                SecurityValidator.logSecurityEvent('invalid_email_format', { username: sanitizedUsername });
                throw new Error('Invalid email format');
            }

            // Rate limiting check
            const clientId = `login_${sanitizedUsername}`;
            if (!SecurityValidator.checkRateLimit(clientId, 5, 300000)) {
                // 5 attempts per 5 minutes
                SecurityValidator.logSecurityEvent('rate_limit_exceeded', { username: sanitizedUsername });
                throw new Error('Too many login attempts. Please try again later.');
            }

            try {
                // Generate CSRF token for this session
                const csrfToken = this.generateCSRFToken();

                const user = await fetchWrapper.post(`${baseUrl}/authenticate`, {
                    username: sanitizedUsername,
                    password,
                    _csrf: csrfToken
                });

                // Reset login attempts on successful login
                this.loginAttempts = 0;
                this.lastLoginAttempt = null;

                // Clear the previous account's cached plan/feature state BEFORE
                // this user's state is published. logout() does this too, but a
                // session can end without logout() ever running (expired token,
                // 401 redirect, or a sign-in from a tab that never saw the
                // sign-out) — and then the old flags would still be in memory.
                // Doing it here as well means the guarantee holds regardless of
                // how the previous session ended.
                await this.resetPerAccountStores();

                // update pinia state
                this.user = user;

                // Re-fetch this account's plan flags now, rather than waiting
                // for a sidebar to mount.
                //
                // Both sidebars call planAccess.load() from mounted() only —
                // there is no watcher — so after the reset above, a layout that
                // does NOT remount would leave flags null. planAccess's getters
                // are permissive when flags are null ("better to flash the menu
                // open than hide everything"), which is a safe default while
                // loading but the wrong answer to leave sitting there: signing
                // into a Freemium account after a paid one would render every
                // paid feature UNLOCKED. Loading explicitly here closes that.
                //
                // The API returns { user: {...}, token }, so the id is nested;
                // fall back to a flat shape defensively.
                const planUserId = (user as any)?.user?.id ?? (user as any)?.id;
                if (planUserId) {
                    try {
                        const { usePlanAccessStore } = await import('@/stores/planAccess');
                        await usePlanAccessStore().load(planUserId);
                    } catch (e) {
                        // Never block sign-in on this. planAccess.load() already
                        // fails safe internally, and the sidebars' mounted()
                        // hooks remain as a second chance.
                        console.warn('Plan flags could not be loaded at login:', e);
                    }
                }

                // Add session metadata for security
                const secureUserData = {
                    ...user,
                    loginTime: Date.now(),
                    sessionId: SecurityValidator.generateCSRFToken(),
                    csrfToken
                };

                // store user details and jwt in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(secureUserData));

                // Identify user in Sentry for session replay & error tracking
                // and enrich PostHog person properties with whatever profile
                // data the backend returned (only defined fields are sent).
                identifySentryUser(
                  String(user.id),
                  user.email || sanitizedUsername,
                  user.name || user.username || '',
                  {
                    company_name: user.company_name || user.company,
                    company_id: user.company_id,
                    user_role: user.role || user.user_role,
                    subscription_tier: user.subscription_tier || user.tier || user.plan,
                    subscription_status: user.subscription_status,
                    signup_date: user.created_at || user.signup_date,
                    is_admin: user.is_admin,
                    is_owner: user.is_owner,
                    team_size: user.team_size,
                    trial_ends_at: user.trial_ends_at,
                    country: user.country,
                  }
                );

                // PostHog: track successful login
                const { track } = useAnalytics();
                track('login_succeeded', { user_id: user.id });

                SecurityValidator.logSecurityEvent('successful_login', { username: sanitizedUsername });

                // Setup Socket.IO for real-time notifications
                try {
                    await socketService.connect(user.token);
                    socketService.authenticate(user.id || user.email, user.token);
                    console.log('✅ Socket.IO connected for user');

                    // Initialize notification store
                    const { useNotificationStore } = await import('@/stores/notifications');
                    const notificationStore = useNotificationStore();
                    await notificationStore.initialize();
                } catch (error) {
                    console.error('Socket.IO setup failed:', error);
                    // Don't block login if Socket.IO fails
                }

                // redirect to previous url or default to home page
                router.push(this.returnUrl || '/dashboard');
            } catch (error) {
                this.loginAttempts++;
                this.lastLoginAttempt = Date.now();
                SecurityValidator.logSecurityEvent('failed_login', {
                    username: sanitizedUsername,
                    attempts: this.loginAttempts,
                    error: error instanceof Error ? error.message : 'Unknown error'
                });

                // PostHog: track failed login (no PII — just the reason category)
                try {
                  const { track } = useAnalytics();
                  track('login_failed', {
                    reason: error instanceof Error ? error.message : 'unknown',
                  });
                } catch (e) {
                  // never let analytics break the auth flow
                }

                throw error;
            }
        },

        /**
         * Clear every store whose contents belong to ONE account.
         *
         * Called on logout AND at the start of a successful login. Login is not
         * redundant: a session can end without logout() ever running — an
         * expired token, a hard 401 redirect, or signing in from a tab that
         * never saw the sign-out — and in those cases the previous account's
         * flags would still be sitting in memory.
         *
         * The implementation is shared with the vuex sign-in/sign-out path
         * (LoginForm.vue / storeVuex's logout action), which is what the app
         * actually uses — see src/utils/resetPerAccountStores.ts.
         */
        async resetPerAccountStores() {
            const { resetPerAccountStores } = await import('@/utils/resetPerAccountStores');
            await resetPerAccountStores();
        },

        clearAllBrowserCache() {
            console.log('🧹 Clearing ALL localStorage items...');
            
            // Get all localStorage keys before clearing
            const allKeys = Object.keys(localStorage);
            console.log(`📋 Found ${allKeys.length} items in localStorage:`, allKeys);
            
            // Explicitly remove critical items first (for logging purposes)
            const criticalItems = [
                '_secure__ls__metadata',
                'user',
                'csrfToken',
                'security_logs',
                'modernize_v3_template'
            ];
            
            criticalItems.forEach(item => {
                if (localStorage.getItem(item)) {
                    localStorage.removeItem(item);
                    console.log(`🔥 Explicitly removed: ${item}`);
                }
            });
            
            // Clear everything from localStorage (preserves costings backups)
            safeClearLocalStorage();
            
            console.log('✅ All localStorage items cleared');
            console.log('🎯 Browser cache cleared successfully');
            
            // Also clear sessionStorage for good measure
            sessionStorage.clear();
            console.log('✅ SessionStorage cleared');
            
            // Clear any IndexedDB data if it exists
            if ('indexedDB' in window) {
                try {
                    // Clear common IndexedDB databases
                    const dbNames = ['webviewer', 'wc-cache', 'app-cache'];
                    dbNames.forEach(async (dbName) => {
                        try {
                            const deleteReq = indexedDB.deleteDatabase(dbName);
                            deleteReq.onsuccess = () => console.log(`✅ IndexedDB '${dbName}' cleared`);
                            deleteReq.onerror = () => console.log(`⚠️ IndexedDB '${dbName}' not found or already cleared`);
                        } catch (e) {
                            console.log(`⚠️ Could not clear IndexedDB '${dbName}':`, e);
                        }
                    });
                } catch (e) {
                    console.log('⚠️ IndexedDB clearing failed:', e);
                }
            }
        },

        async logout() {
            if (this.user) {
                const sessionDuration = this.user.loginTime ? Date.now() - this.user.loginTime : 0;

                SecurityValidator.logSecurityEvent('user_logout', {
                    username: this.user.username || 'unknown',
                    sessionDuration
                });

                // PostHog: track logout with session duration so we can build
                // a "session length distribution" chart in PostHog.
                try {
                  const { track } = useAnalytics();
                  track('logout', { session_duration_ms: sessionDuration });
                } catch (e) {
                  // never let analytics break logout
                }
            }

            // Disconnect Socket.IO on logout
            try {
                // Cleanup notification store
                const { useNotificationStore } = await import('@/stores/notifications');
                const notificationStore = useNotificationStore();
                notificationStore.cleanup();

                // Disconnect socket
                socketService.disconnect();
                console.log('✅ Socket.IO disconnected');
            } catch (error) {
                console.error('Socket.IO logout failed:', error);
            }

            // Clear Sentry user context
            resetSentryUser();

            // Drop every per-account Pinia store.
            //
            // These live in MEMORY for the lifetime of the page, not the
            // session, and each one gates its fetch behind a `loaded` flag —
            // planAccess.load() and uploadConfig.ensureLoaded() both return
            // immediately once loaded. Clearing localStorage below does not
            // touch them.
            //
            // The symptom: sign out of a Freemium account, sign into a paid
            // one, and planAccess still holds the Freemium flags, so every
            // gated feature stays locked until the browser is refreshed —
            // a refresh being the only thing that rebuilt the Pinia stores.
            await this.resetPerAccountStores();

            this.user = null;
            this.csrfToken = null;
            this.loginAttempts = 0;

            // Clear all authentication-related storage
            localStorage.removeItem('user');
            localStorage.removeItem('csrfToken');
            localStorage.removeItem('notification_permission_requested'); // Reset notification modal for next login

            // Clear ALL browser cache and localStorage items
            this.clearAllBrowserCache();
            
            router.push('/');
        },

        checkSessionTimeout() {
            if (this.user && this.user.loginTime) {
                const sessionDuration = Date.now() - this.user.loginTime;
                const maxSessionTime = 8 * 60 * 60 * 1000; // 8 hours

                if (sessionDuration > maxSessionTime) {
                    SecurityValidator.logSecurityEvent('session_timeout', {
                        username: this.user.username || 'unknown',
                        sessionDuration
                    });
                    this.logout();
                    return false;
                }
            }
            return true;
        }
    }
});
